import React, { useState } from "react";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";
import { Upload, Download } from "lucide-react";

const SalaryImportExport = () => {
  const [activeTab, setActiveTab] = useState("import");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const handleExport = async () => {
    setLoading(true);
    try {
      const blob = await payrollService.exportSalaryStructures();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `salary_structures_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setNotification({ type: "success", message: "Salary structures exported successfully" });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to export data" });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setNotification({ type: "warning", message: "Please select a file" });
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await payrollService.importSalaryStructures(formData);
      if (response.success) {
        setNotification({
          type: "success",
          message: `Imported ${response.data.success} records`,
        });
        setFile(null);
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to import data" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Salary Import/Export
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Import or export salary structures
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {["import", "export"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === tab
                ? "bg-blue-600 text-white"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === "import" ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Import Salary Structures
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Excel File
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              onClick={handleImport}
              disabled={loading || !file}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              <Upload size={18} />
              {loading ? "Importing..." : "Import"}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Export Salary Structures
          </h3>
          <button
            onClick={handleExport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            <Download size={18} />
            {loading ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SalaryImportExport;

